import { type Dispatch, type SetStateAction } from "react";

import { type NodeModelWithId } from "@doitintl/cloudflow-commons";
import {
  type ActionEntityModel,
  type AppKey,
  type ApprovalConfig,
  type CloudflowCreationStatus,
  type CloudflowEntityModel,
  type CloudFlowNodeType,
  type CloudFlowProvider,
  type CloudflowType,
  type INodeModel,
  type NodeConfigApiParametersBase,
  type NodeModel,
  type NodeParameters,
  type NodeStatus,
  type NodeTransition,
  type NodeTransitionList,
  type ObjectValues,
} from "@doitintl/cmp-models";
import { type ModelReference } from "@doitintl/models-any";
import { type Reference } from "@doitintl/models-types";
import {
  type Edge,
  type EdgeMouseHandler,
  type Node,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";

import {
  type useAttachBlueprint,
  type useCreateOrUpdateNode,
  type useDeleteNode,
  type usePublishCloudflow,
  type useReplaceNodes,
  type useTriggerCloudflow,
  type useUnpublishCloudflow,
  type useUpdateCloudflow,
  type useUpdateCloudflowNodes,
} from "./hooks";

export type CloudflowLink = {
  title: string;
  url: string;
};

export type CloudflowKindText = {
  subtitle: string;
  title: string;
};

export enum CloudflowKind {
  fromScratch = "fromScratch",
  blueprint = "blueprint",
}

export type CloudflowBlueprint = {
  id: string;
  name: string;
  description: string;
  integrations: Readonly<Array<Integration>>;
  tags?: Record<string, string[]>;
  ref: ModelReference<CloudflowEntityModel>;
};

export type Cloud = Omit<AppKey, "internal">;
export type Integration = "slack" | "jira" | "email";

export type DeleteCloudflowInput = {
  id: string;
};

export type CreateCloudflowInput = {
  name: string;
  firstNodeId: string;
  description?: string;
  status?: CloudflowCreationStatus;
  nodes: BaseNodeData[];
  collaborators: {
    email: string;
    role: "owner";
  }[];
  type?: "demo" | "custom";
};

export type ReplaceNodesInput = {
  deleted: {
    nodeId: string;
    shouldCascadeDelete: boolean;
  };
  created: {
    data: CreateOrUpdateNode;
  };
};

export type UpdateCloudflowInput = {
  name?: string;
  description?: string;
  status?: CloudflowCreationStatus;
};

export type AttachBlueprintInput = { targetNodeId: string; blueprintId: string; transition?: NodeTransition };

export type TriggerCloudflowResponce = {
  flowId: string;
  customerId: string;
  message: string;
  triggeredBy: string;
};

export type TestNodeResponse = { isError: boolean; result: unknown };

export type CloudFlowNode<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> = NodeModel<TNodeType> & {
  id: string;
};

export type CloudflowDTO = {
  id: string;
  name: string;
  description: string;
  status: CloudflowCreationStatus;
  customer: string;
  createdBy: string;
  firstNode: string | null;
  createdAt: Date;
  updatedAt: Date;
  type: CloudflowType;
  nextRun?: Date | null;
  nodes: CloudFlowNode[];
};

export type PartialUpdateNodesList = Array<Partial<CloudFlowNode>>;

export type PartialNodeUpdates<T> = Omit<T, "updatedNodes"> & {
  updatedNodes: PartialUpdateNodesList;
};

export type CreateNodeResponseDTO = {
  addedNode?: CloudFlowNode;
  updatedNodes: CloudFlowNode[];
};

export type UpdateNodeResponseDTO = {
  updatedNode: CloudFlowNode;
};

export type CreateOrUpdateNodesResponseDTO = {
  addedNodes: CloudFlowNode[];
  updatedNodes: CloudFlowNode[];
};

export type ReplaceNodesResponseDTO = {
  deleted: DeleteNodesResponseDTO | null;
  created: CreateNodeResponseDTO | null;
};

export type PublishCloudFlowResponseDTO = {
  addedNodes: CloudFlowNode[];
  updatedNodes: CloudFlowNode[];
  cloudflow: CloudflowDTO | null;
};

export type DeleteNodesResponseDTO = {
  deletedNodes?: string[];
  updatedNodes: CloudFlowNode[];
};

export type GetNodeAuditDetailsDTO = {
  parameters: ExecutionNodeParameters | null;
  results: ExecutionNodeResults | null;
};

export type ExecutionNodeParameters = unknown[];

export type ExecutionNodeResults = unknown[];

export type TestNodeRequest = {
  cloudflowId: string;
  nodeId: string;
  input: {
    params: NodeConfigApiParametersBase;
  };
};

export type ConfigurationTab =
  | "Parameters"
  | "Permissions"
  | "Test"
  | "Schedule"
  | "FilterParameters"
  | "TransformParameters"
  | "ManualTrigger";

export interface NodeConfigs<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> {
  name: string;
  parameters: NodeParameters<TNodeType>;
  approval?: ApprovalConfig;
  type: TNodeType;
  id: string;
  flowId: string;
  status: NodeStatus;
  transitions: NodeTransitionList;
  touched: boolean;
  errors: Partial<Record<ErrorKeys, string>>;
}

export enum PermissionValidityStatus {
  VALID = "VALID",
  UNABLE_TO_VERIFY = "UNABLE_TO_VERIFY",
  INVALID = "INVALID",
  INITIAL = "INITIAL",
}

// create a discriminated union of all the NodeConfigs so that we can properly
// narrow NodeConfigs
export type AllNodeConfigs = {
  [T in CloudFlowNodeType]: NodeConfigs<T>;
}[CloudFlowNodeType];

/**
 * PermissionCheckResult contains the result of a permissions check.
 * It is an object when checking a single account for a permission
 * It is an array when checking all/multiple accounts for a permission
 */
export type PermissionCheckData =
  | {
      requiredPermissions: string[];
      command: string;
    }
  | {
      accountName: string;
      requiredPermissions: string[];
      command: string;
      hasPermission: boolean;
    }[];

export type PermissionCheckResult =
  | {
      data: undefined;
      status: PermissionValidityStatus.INITIAL;
    }
  | {
      data: undefined;
      status: PermissionValidityStatus.VALID;
    }
  | {
      data: PermissionCheckData;
      status: PermissionValidityStatus.INVALID;
    }
  | {
      data: undefined;
      status: PermissionValidityStatus.UNABLE_TO_VERIFY;
    };

export type CloudPermissions = {
  result: PermissionCheckResult;
  updatePermissions: () => Promise<void>;
  resetPermissions: () => void;
  loading: boolean;
};

export type PermissionMap = {
  [serviceName: string]: boolean;
};

export type Position = {
  x: number;
  y: number;
};

export const EDGE_TYPE = {
  CUSTOM: "custom",
  GHOST: "ghost",
  CONDITION: "condition",
} as const;

export type EdgeType = ObjectValues<typeof EDGE_TYPE>;

export type BaseNodeData<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> = {
  id: string;
  actionID?: null | Reference<ActionEntityModel>;
} & Omit<INodeModel<TNodeType>, "action" | "createdBy">;

export type CreateOrUpdateNode<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> = {
  transition?: {
    parentNodeId: string;
    targetNodeId: string;
    label?: string;
    value?: any;
  };
  node: BaseNodeData<TNodeType>;
};

export type UpdateCloudflowNodes = {
  name?: string;
  flowId: string;
  addedNodes?: CreateOrUpdateNode[];
  updatedNodes?: CreateOrUpdateNode[];
};

export interface RFNode<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> extends Record<string, unknown> {
  nodeData: INodeModel<TNodeType>;
  touched: boolean;
  stepNumber?: number;
  onEditNode?: () => void;
  onDeleteNode?: () => void;
  onAddNode?: (nodeType: CloudFlowNodeType, id: string, position?: Position) => void;
  isBlueprintPresentationMode?: boolean;
}

export enum CHANGE_TRIGGER_OPTIONS {
  SCHEDULED = "Scheduled",
  MANUAL = "Manual",
  NONE = "None",
  WEBHOOK = "Webhook",
  SYSTEM_EVENT = "SystemEvent",
}

export interface CloudflowOperationArgs {
  createOrUpdate: [customerId: string, flowId: string, data: CreateOrUpdateNode];
  replaceNodes: [customerId: string, flowId: string, input: ReplaceNodesInput];
  updateCloudflow: [customerId: string, flowId: string, updateCloudflowInput: UpdateCloudflowInput];
  remove: [customerId: string, flowId: string, nodeId: string];
  updateNodes: [customerId: string, nodes: UpdateCloudflowNodes];
  publishCloudflow: [customerId: string, flowId: string, unsavedNodes?: UpdateCloudflowNodes];
  unpublishCloudflow: [customerId: string, flowId: string];
  triggerCloudflow: [customerId: string, flowId: string];
  attachBlueprint: [input: AttachBlueprintInput];
}

export type CloudflowOperationContextType = {
  createOrUpdate: ReturnType<typeof useCreateOrUpdateNode>[0];
  replaceNodes: ReturnType<typeof useReplaceNodes>[0];
  updateCloudflow: ReturnType<typeof useUpdateCloudflow>[0];
  remove: ReturnType<typeof useDeleteNode>[0];
  updateNodes: ReturnType<typeof useUpdateCloudflowNodes>[0];
  publishCloudflow: ReturnType<typeof usePublishCloudflow>[0];
  unpublishCloudflow: ReturnType<typeof useUnpublishCloudflow>[0];
  triggerCloudflow: ReturnType<typeof useTriggerCloudflow>[0];
  attachBlueprint: ReturnType<typeof useAttachBlueprint>[0];
  httpOperationLoading: boolean;
};

export type BaseCloudflowHit = {
  objectID: string;
  provider: CloudFlowProvider;
  serviceName: string;
  serviceNameShort: string;
  versionId: string;
  operationName: string;
  operationDoc?: string;
  serviceIcon?: string;
  operationLabel?: string;
};

export type NodeEdgeManagerConfig = {
  setNodes: Dispatch<SetStateAction<Node<RFNode>[]>>;
  activeNode?: Node<RFNode>;
  nodes: Node<RFNode>[];
  edges: Edge[];
  getEdges: () => Edge[];
  getNodes: () => Node<RFNode<CloudFlowNodeType>>[];
  selectNode: (nodeId: string) => void;
  handleEditNode: (node: Node<RFNode>) => void;
  onConnect: OnConnect;
  onEdgeClick: EdgeMouseHandler<Edge>;
  operationType: NodeOperationType;
  onConfirmChangeTrigger: (choice: CHANGE_TRIGGER_OPTIONS) => void;
  onConfirmDeleteIfNode: () => Promise<void>;
  deleteIfNodeId: string;
  setDeleteIfNodeId: Dispatch<SetStateAction<string>>;
  manageIfActionsId: string;
  setManageIfActionsId: Dispatch<SetStateAction<string>>;
  onSaveManageIfActionsDialog: (choice: string) => void;
  handleAddNode: (nodeType: CloudFlowNodeType, nodeId: string) => void;
  onChangeActiveNode: (nodeType: CloudFlowNodeType, nodeId: string) => void;
  saveCloudFlow: (customerId: string, nodes: UpdateCloudflowNodes) => Promise<void>;
  publishCloudFlow: (customerId: string, flowId: string, nodes: UpdateCloudflowNodes | undefined) => Promise<void>;
  interactionEnabled: boolean;
  handleNodeOperation: (nodeId: string, item: BaseCloudflowHit, operation: NodeOperationType) => void;
  onNodesChange: OnNodesChange<Node<RFNode>>;
  onEdgesChange: OnEdgesChange;
  handleAddBlueprint: (nodeId: string, blueprintId: string) => Promise<void>;
  nodesWithReferencedNode: Node<RFNode<CloudFlowNodeType>>[];
  handleDeleteReferencedNode: () => Promise<void>;
  selectedProvider?: CloudFlowProvider;
};

export type Region = {
  Endpoint: string;
  OptInStatus: string;
  RegionName: string;
};

export type EdgeData = {
  label: string;
  target: string;
  setInteractionEnabled: (value: boolean) => void;
  handleAddNode: (nodeType: CloudFlowNodeType, targetNodeId: string, provider?: CloudFlowProvider) => void;
  handleAttachBlueprint: (targetNodeId: string) => void;
};

export type UpdateNodeData<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> = Partial<
  Pick<NodeConfigs<TNodeType>, "name" | "status" | "approval" | "errors" | "parameters">
>;

export type HandleUpdateNodeFn<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> = (
  nodeId: string,
  updateNodeAction: UpdateNodeData<TNodeType> | ((prevNode: UpdateNodeData<TNodeType>) => UpdateNodeData<TNodeType>)
) => void;

// Defines the keys to ne used for node config errors
export type ErrorKeys = "param_error" | "permission_error" | "approval_error";

export enum NodeOperationType {
  UPDATE = "update",
  CREATE = "create",
}

export type ProjectInfo = {
  projectId: string;
  projectNumber: string;
};

export type OrganizationOrProject = {
  id: string;
  displayName: string;
  scope: "organization" | "project";
};

// Types for event tracking
type CloudflowBaseEvent = {
  flowId: string;
};

export type BaseNodeEvent =
  | { nodeType: CloudFlowNodeType }
  | {
      nodeType: CloudFlowNodeType.ACTION;
      apiName: string;
      apiProvider: string;
    };

export type CloudflowCreatedEvent = CloudflowBaseEvent &
  ({ source: "blueprint"; cloudProvider: string[]; blueprintId: string } | { source: "new" | "edit" });

export type NodeTestedEvent = CloudflowBaseEvent &
  BaseNodeEvent & {
    testSuccessful: boolean;
  };

export type NodeCreatedEvent = CloudflowBaseEvent & BaseNodeEvent;

export type CloudflowPublishedEvent = CloudflowBaseEvent & {
  isPublished: boolean;
};

export type UpsellButtonClickedEvent = {
  customerId: string;
};

export type NodeEditedEvent = CloudflowBaseEvent &
  BaseNodeEvent & {
    actionType: "edit";
    hadIssues: boolean;
  };

export type NodeModelWithIdAndStep<TNodeType extends CloudFlowNodeType = CloudFlowNodeType> =
  NodeModelWithId<TNodeType> & {
    stepNumber?: number;
  };

export type DialogType =
  | "action"
  | "blueprint"
  | "trigger"
  | "removeReferencedNode"
  | "editCloudflowTitle"
  | "editNodeTitle";
